import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Header = ({ setIsLoginOpen }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      if (!isHomePage) {
        setScrolled(true);
      } else {
        setScrolled(window.scrollY > 50);
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: "The Problem", href: "#the-problem" },
    { name: "Form Builder", href: "#form-builder" },
    { name: "Why ContactHQ", href: "#why-love-it" },
    { name: "Inbox", href: "#centralized-inbox" },
    { name: "How it Works", href: "#how-it-works" },
    { name: "Use Cases", href: "#use-cases" },
    { name: "FAQ", href: "#faq" },
    { name: "Testimonials", href: "#testimonials" },
  ];

  return (
    <header
      className={`fixed top-0 z-[100] w-full transition-all duration-500 ${
        scrolled ? "py-2 sm:py-3" : "py-4 sm:py-6"
      }`}
    >
      <div
        className={`mx-auto w-[95%] md:w-[90%] transition-all duration-500 rounded-2xl border border-transparent flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-6 min-h-[56px] ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-[#E2E8F0] shadow-lg py-2 sm:py-3"
            : ""
        }`}
      >
        {/* LOGO */}
        <Link
          to="/"
          className="flex items-center gap-2 sm:gap-3 group cursor-pointer shrink-0 min-w-0"
        >
          <div className="flex items-center justify-center size-9 sm:size-10 shrink-0 rounded-xl bg-[#E63946] text-white shadow-lg shadow-[#E63946]/20 group-hover:rotate-6 transition-transform">
            <span className="material-symbols-outlined text-xl sm:text-2xl">
              hub
            </span>
          </div>
          <span className="text-lg sm:text-xl font-black tracking-tighter text-[#0B1C2D] truncate">
            ContactHQ
          </span>
        </Link>

        {/* DESKTOP CENTER NAV */}
        {isHomePage && (
          <nav className="hidden xl:flex items-center gap-1 bg-slate-100/50 p-1 rounded-full border border-[#E2E8F0]/50">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="px-4 py-2 text-[12px] font-bold text-[#64748B] hover:text-[#0B1C2D] hover:bg-white rounded-full transition-all duration-300 whitespace-nowrap"
              >
                {link.name}
              </a>
            ))}
          </nav>
        )}

        {/* ACTIONS */}
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {/* Desktop / tablet actions — hide on home mobile where hamburger owns them */}
          <button
            type="button"
            onClick={() => setIsLoginOpen(true)}
            className={`${
              isHomePage ? "hidden xl:inline-flex" : "inline-flex"
            } items-center text-sm font-bold text-[#64748B] hover:text-[#E63946] px-2 sm:px-4 whitespace-nowrap`}
          >
            Log In
          </button>

          <Link
            to="/register"
            className={`${
              isHomePage ? "hidden xl:inline-flex" : "inline-flex"
            } items-center gap-2 rounded-xl bg-[#0B1C2D] px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-bold text-white hover:bg-[#E63946] transition-all duration-300 shadow-xl shadow-[#0B1C2D]/10 whitespace-nowrap`}
          >
            Get Started
            <span className="material-symbols-outlined text-sm">
              arrow_forward
            </span>
          </Link>

          {/* Mobile menu toggle (home only) */}
          {isHomePage && (
            <button
              type="button"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              className="xl:hidden flex items-center justify-center size-10 shrink-0 rounded-lg bg-slate-100 text-[#0B1C2D]"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <span className="material-symbols-outlined">
                {mobileMenuOpen ? "close" : "menu"}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* MOBILE MENU */}
      {mobileMenuOpen && isHomePage && (
        <div className="xl:hidden absolute top-full left-0 w-full p-3 sm:p-4 animate-fade-in-up">
          <div className="bg-white border border-[#E2E8F0] rounded-3xl shadow-2xl p-5 sm:p-6 space-y-2 max-h-[min(80vh,640px)] overflow-y-auto">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base sm:text-lg font-bold text-[#0B1C2D] hover:text-[#E63946] py-2.5 border-b border-slate-50"
              >
                {link.name}
              </a>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsLoginOpen(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full py-3.5 rounded-xl bg-slate-100 font-bold text-[#0B1C2D] whitespace-nowrap"
              >
                Log In
              </button>
              <Link
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full py-3.5 rounded-xl bg-[#E63946] font-bold text-white text-center whitespace-nowrap"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
